import {
    ConstructionNumberModel,
    ConstructionNumberSpecifications,
    DecimalRange,
    ProjectPhaseModel,
    PropertySpecifications,
    PropertyTypeModel,
} from '../../generated';
import { mergeDecimalRange } from './merge-decimal-range';

type PropertyKeys = keyof PropertySpecifications;

interface MergeSpecificationRange {
    (obj: { propertyTypes: PropertyTypeModel[] }, key: PropertyKeys): DecimalRange;
    (obj: { projectPhases: ProjectPhaseModel[] }, key: PropertyKeys): DecimalRange;
}

const mergeSpecification = <Specifications>(
    range: DecimalRange,
    key: keyof Specifications,
    specifications?: Specifications,
): DecimalRange => {
    if (specifications && specifications[key]) {
        return mergeDecimalRange(range, specifications[key]!);
    }

    return range;
};

/**
 * Merges all specification properties of an array of apiObjects:
 *
 * @param {{
 *         propertyTypes?: PropertyTypeModel[];
 *         projectPhases?: ProjectPhaseModel[];
 *     }} {
 *         propertyTypes,
 *         projectPhases,
 *     }
 * @param {keyof PropertySpecifications} key
 * @return {*}  {DecimalRange}
 */
const mergePropertySpecification: MergeSpecificationRange = (
    {
        propertyTypes,
        projectPhases,
    }: {
        propertyTypes?: PropertyTypeModel[];
        projectPhases?: ProjectPhaseModel[];
    },
    key: keyof PropertySpecifications,
): DecimalRange => {
    if (propertyTypes) {
        return propertyTypes.reduce<DecimalRange>((range, propertyType) => {
            return mergeSpecification<PropertySpecifications>(
                range,
                key,
                propertyType.specifications,
            );
        }, {});
    }

    if (projectPhases) {
        return projectPhases.reduce<DecimalRange>((range, projectPhase) => {
            return mergeSpecification<PropertySpecifications>(
                range,
                key,
                projectPhase.propertySpecifications,
            );
        }, {});
    }

    return {};
};

/**
 * Merges all specification properties of an array of constructionNumbers
 *
 * @param {ConstructionNumberModel[]} constructionNumbers
 * @param {keyof ConstructionNumberSpecifications} key
 * @return {*}  {DecimalRange}
 */
const mergeConstructionNumberSpecification = (
    constructionNumbers: ConstructionNumberModel[],
    key: keyof ConstructionNumberSpecifications,
): DecimalRange => {
    return constructionNumbers.reduce<DecimalRange>((range, constructionNumber) => {
        return mergeSpecification<ConstructionNumberSpecifications>(
            range,
            key,
            constructionNumber.specifications,
        );
    }, {});
};

export { mergePropertySpecification, mergeConstructionNumberSpecification };
